import React, { useEffect, useState } from "react";
import style from "../documentConfig/documentConfig.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import Table, { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { ErrorToast } from "../../components/toastMessage";
import axios from "axios";
import PageHeader from "../../components/pageHeader/pageHeader";
import { useDebounce } from "../../hooks/common";
import Paginator, { PaginatorForRDT } from "../../components/CommonComponents/Paginator/Paginator";
import { BASE_URL } from "../../constants/Constants";
import { formatDate } from "../../utils/date";
import Edit from "../../assets/EditInWhite.svg";
import Dropdown from "react-bootstrap/esm/Dropdown";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EditButton, { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import { _axios } from "../../utils/axios";

const DocumentConfig = () => {
  const { state } = useLocation();
  const [totalList, setTotalList] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [lastDate, setLastDate] = useState<string | null>(null);
  const [documentConfigList, setDocumentConfigList] = useState([]);
  const [declarationConfigList, setDeclarationConfigList] = useState([]);
  const [selectedDeclarationFilters, setSelectedDeclarationFilters] = useState<string[]>([]);
  const [declarationLastDate, setDeclarationLastDate] = useState<string | null>(null);
  const [tab, setTab] = useState(state?.tab ?? 1);
  const [isGridView, setIsGridView] = useState(false);
  const search = useDebounce(searchQuery);
  const navigate = useNavigate();

  const getDocumentConfigList = () => {
    _axios.post(`${BASE_URL}/api/document-config/list`,
      {
        page: currentPage,
        limit: entriesPerPage,
        search: search,
        filters: selectedFilters,
        lastDate
      }
    )
      .then(function (response) {
        if (response?.data?.success === 0) {
          ErrorToast(response?.data?.message)
          return
        }
        setTotalList(response.data.data.total_rows)
        setDocumentConfigList(response.data.data.results)

        // handle success
        console.log(response);
      })
      .catch(function (error: any) {
        // handle error
        console.log(error);
      })
      .finally(function () {
        // always executed
      });
  }

  const getDeclarationConfigList = () => {
    _axios.post(`${BASE_URL}/api/declaration-config/list`,
      {
        page: currentPage,
        limit: entriesPerPage,
        search: search,
        filters: selectedDeclarationFilters,
        lastDate: declarationLastDate
      }
    )
      .then(function (response) {
        if (response?.data?.success === 0) {
          ErrorToast(response?.data?.message)
          return
        }
        setTotalList(response.data.data.total_rows)
        setDeclarationConfigList(response.data.data.results)

        // handle success
        console.log(response);
      })
      .catch(function (error: any) {
        // handle error
        console.log(error);
      })
      .finally(function () {
        // always executed
      });
  }

  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery('');
    if (tab === 1) getDocumentConfigList();
    else getDeclarationConfigList();
  }, [tab]);

  useEffect(() => {
    if (tab === 1) getDocumentConfigList();
    else getDeclarationConfigList();
  }, [currentPage, search, selectedFilters, lastDate, selectedDeclarationFilters, declarationLastDate]);


  const DocumentColumns = [
    {
      name: 'Sr. No.',
      width: "125px",
      // selector: (row: any, rowIndex: number | undefined) => (currentPage - 1) * entriesPerPage + (rowIndex ?? 0) + 1,
      selector: (log: any, rowIndex: number | undefined) => {
        const serialNumber = (currentPage - 1) * entriesPerPage + (rowIndex ?? 0) + 1;
        return serialNumber.toString().padStart(3, '0');
      },
      grow: 0.5,
      sortable: false,
    },
    {
      name: 'Document Name',
      selector: (row: any) => row.document_name || "-",
      cell: (row: any) => (
          <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '300px',
                padding: '8px 4px'
              }}
              title={row.document_name || "-"}
          >
            {row.document_name || "-"}
          </div>
      ),
      sortable: true,
      grow: 2,
    },
    {
      name: 'Form Type',
      width: "175px",
      grow: 0.5,
      selector: (row: any) => row.document_Type || "-",
      cell: (row: any) => (<span className="text-center">{row.document_Type || "-"}</span>),
      sortable: true,
    },
    {
      name: 'Last Publish Date',
      grow: 1,
      selector: (row: any) => formatDate(new Date(row.updated_date)),
      sortable: true,
      width: "220px",
      sortFunction: (a: any, b: any) => {
        const dateA = new Date(a.updated_date);
        const dateB = new Date(b.updated_date);
        return dateA.getTime() - dateB.getTime();
      }
    },
    {
      name: 'Status',
      selector: (row: any) => row.active,
      width: "145px",
      grow: 0.5,
      cell: (row: any) => (
        <button
          className={row.active === 1 ? "activeButtonCss" : "inactiveButtonCss"}
        >
          {row.active === 1 ? "Active" : "Inactive"}
        </button>
      ),
      sortable: true,
    },
    {
      name: 'Created By',
      selector: (row: any) => row.created_by,
      cell: (row: any) => <span className="text-center">{row.created_by}</span>,
      sortable: true,
      width: "170px",
    },
    {
      name: 'Created On',
      selector: (row: any) => formatDate(new Date(row.created_on)),
      sortable: true,
      width: "170px",
    },
    {
      name: 'Modified By',
      selector: (row: any) => row.modified_by,
      cell: (row: any) => <span className="text-center">{row.modified_by}</span>,
      sortable: true,
      width: "170px",
    },
    {
      name: 'Modified On',
      selector: (row: any) => formatDate(new Date(row.modified_on)),
      sortable: true,
      width: "170px",
    },
    {
      name: 'Action',
      width: "125px",
      grow: 0.5,
      cell: (row: any) => (
        <div className="flex space-x-2">
          <EditButton
            onClick={() => { navigate("/new-document", { state: { documentData: row } }) }}
          />
          <ViewButton
            disabled={true}
            onClick={function (): void {
              throw new Error("Function not implemented.");
            }}
          />
        </div>
      ),
      sortable: false,
    },
  ];

  const DeclarationColumns = [
    {
      name: 'Sr. No.',
      grow: 0.5,
      // selector: (row: any, rowIndex: number | undefined) => (currentPage - 1) * entriesPerPage + (rowIndex ?? 0) + 1,
      selector: (log: any, rowIndex: number | undefined) => {
        const serialNumber = (currentPage - 1) * entriesPerPage + (rowIndex ?? 0) + 1;
        return serialNumber.toString().padStart(3, '0');
      },
      sortable: false,
    },
    {
      name: 'Declaration Name',
      grow: 2,
      cell: (row: any) => (<span className="text-center">{row.declaration_name || "-"}</span>),
      selector: (row: any) => row.declaration_name,
      sortable: true,
    },
    {
      name: 'Form Type',
      width: "165px",
      grow: 0.5,
      cell: (row: any) => (<span className="text-center">{row.form_type || "-"}</span>),
      selector: (row: any) => row.form_type,
      sortable: true,
    },
    {
      name: 'Last Publish Date',
      grow: 1,
      selector: (row: any) => formatDate(new Date(row.updated_date)),
      sortable: true,
      width: "220px",
    },
    {
      name: 'Status',
      width: "145px",
      grow: 0.5,
      selector: (row: any) => row.active,
      cell: (row: any) => (
        <button
          className={row.active === 1 ? "activeButtonCss" : "inactiveButtonCss"}
        >
          {row.active === 1 ? "Active" : "Inactive"}
        </button>
      ),
      sortable: true,
    },
    {
      name: 'Created By',
      selector: (row: any) => row.created_by,
      cell: (row: any) => <span className="text-center">{row.created_by}</span>,
      sortable: true,
      width: "170px",
    },
    {
      name: 'Created On',
      selector: (row: any) => formatDate(new Date(row.created_on)),
      sortable: true,
      width: "170px",
    },
    {
      name: 'Modified By',
      selector: (row: any) => row.modified_by,
      cell: (row: any) => <span className="text-center">{row.modified_by}</span>,
      sortable: true,
      width: "170px",
    },
    {
      name: 'Modified On',
      selector: (row: any) => formatDate(new Date(row.modified_on)),
      sortable: true,
      width: "170px",
    },
    {
      name: 'Action',
      grow: 0.5,
      cell: (row: any) => (
        <div className="flex space-x-2">
          <EditButton
            onClick={() => { navigate("/new-declaration", { state: { declarationData: row } }) }}
          />
          <ViewButton
            disabled={true}
            onClick={function (): void {
              throw new Error("Function not implemented.");
            }}
          />
        </div>
      ),
      sortable: false,
    },
  ];

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Adjust for timezone issues, if any
      const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      const dateString = adjustedDate.toISOString().split('T')[0];
      setLastDate(dateString); // Update selected date
      setDeclarationLastDate(dateString);
      getDeclarationConfigList();; // Call your function to handle the date filter change
    } else {
      setLastDate(null); // Handle clearing the date
    }
  };

  const dropdownContent = (
    <div className={style.dropdownBox}>
      <ul>
        <Dropdown.Menu show className={style.DropDropMenu}>
          <div className={style.dropContent}>
            <Dropdown.Header className={style.DropTile}>
              {tab === 1 ? "Document Config filter" : "Declaration Config filter"}
            </Dropdown.Header>
            <ul>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    className={style.checkBoxDrop}
                    checked={selectedFilters.includes('1')}
                    onChange={() => handleFilterChange("Active")} // Update filter options
                  />{" "} <label >Active Status</label>
                </div>
              </li>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    className={style.checkBoxDrop}
                    checked={selectedFilters.includes('0')}
                    onChange={() => handleFilterChange("Inactive")} // Update filter options
                  />{" "}Inactive Status
                </div>
              </li>
              <li>
                <span className="text-sm pl-2"> Last Publish Date</span>
                <DatePicker
                  selected={lastDate ? new Date(lastDate) : null} // Convert string to Date object
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  className="border border-black"
                  isClearable // Enables built-in clear button
                />
              </li>
            </ul>
          </div>
        </Dropdown.Menu>
      </ul>
    </div>
  );

  const handleFilterChange = (filter: string, value?: string) => {
    console.log("Filter:", filter);
    console.log("Value:", value);

    let filterValue;

    if (filter === "Active") {
      filterValue = "1";
    } else if (filter === "Inactive") {
      filterValue = "0";
    } else {
      filterValue = filter;
    }

    if (tab === 1) {
      if (filter !== 'lastDate') {
        const index = selectedFilters.indexOf(filterValue);
        if (index === -1) {
          setSelectedFilters([...selectedFilters, filterValue]);
        } else {
          const updatedFilters = [...selectedFilters];
          updatedFilters.splice(index, 1);
          setSelectedFilters(updatedFilters);
        }
      }

      if (filter === 'lastDate' && value) {
        console.log("Selected Last Date:", value);
        setLastDate(value);
        getDocumentConfigList();
      }
    } else if (tab === 2) {
      if (filter !== 'lastDate') {
        const index = selectedDeclarationFilters.indexOf(filterValue);
        if (index === -1) {
          setSelectedDeclarationFilters([...selectedDeclarationFilters, filterValue]);
        } else {
          const updatedFilters = [...selectedDeclarationFilters];
          updatedFilters.splice(index, 1);
          setSelectedDeclarationFilters(updatedFilters);
        }
      }
    }
  };


  return (
    <div className="p-8 overflow-auto">
      <PageHeader
        title="Document Config"
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showSearchButton={true}
        showAdditionalButton={true}
        buttonTitle={'New ' + (tab === 1 ? 'Document' : 'Declaration')}
        onClick={() => { tab === 1 ? navigate("/new-document") : navigate("/new-declaration") }}
        isGridView={isGridView}
        onGridIconClick={() => setIsGridView(!isGridView)}
        showFilterButton={true}
        dropdownContent={dropdownContent}
      />
      <div className={style.verificationbox}>
        <div>
          <div className="d-flex" style={{ paddingBottom: "0" }}>
            <p onClick={() => setTab(1)}>
              <button className={tab === 1 ? style.topRoundedButton2 : style.topRoundedButton1}>Document</button>
            </p>
            <p onClick={() => setTab(2)}>
              <button className={tab === 2 ? style.topRoundedButton2 : style.topRoundedButton1}>Declaration</button>
            </p>
          </div>
        </div>
        {tab === 1 && (isGridView ?
          <>
            <BoxComponent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {documentConfigList?.map((row: any) => {
                  return (
                    <div className="p-4 rounded-2xl bg-white flex flex-col shadow-lg border border-gray-300">
                      <div className="flex justify-between items-center space-x-4 mb-4">
                        <button className={row.active === 1 ? "activeButtonCss" : "inactiveButtonCss"}>
                          {row.active === 1 ? "Active" : "Inactive"}
                        </button>
                        {/* <img src={Edit} alt="Edit" onClick={() => navigate("/new-document", { state: { documentData: row } })} className="globalEditCss" /> */}
                        <div className="flex space-x-2">
                          <EditButton
                            onClick={() => navigate("/new-document", { state: { documentData: row } })}
                          />
                          <ViewButton
                            disabled={true}
                            onClick={function (): void {
                              throw new Error("Function not implemented.");
                            }}
                          />
                        </div>
                      </div>
                      <div className="mb-2 flex text-[12px]">
                        <p className="font-bold text-base w-1/2">Document Name</p>
                        <p className="text-base mx-2">:</p>
                        <p className="text-base w-2/3">{row?.document_name}</p>
                      </div>
                      <div className="mb-2 flex text-[12px]">
                        <p className="font-bold text-base w-1/2">Form Type</p>
                        <p className="text-base mx-2">:</p>
                        <p className="text-base w-2/3">{row?.document_Type ?? ''}</p>
                      </div>
                      <div className="mb-2 flex text-[12px]">
                        <p className="font-bold text-base w-1/2">Last Publish Date</p>
                        <p className="text-base mx-2">:</p>
                        <p className="text-base w-2/3">{formatDate(row?.updated_date)}</p>
                      </div>
                    </div>
                  )
                })
                }
              </div>
              <Paginator
                limit={entriesPerPage}
                page={currentPage}
                setPage={setCurrentPage}
                count={totalList || 0}
              />
            </BoxComponent>
          </>
          : <BoxComponent>
            <Table
              columns={DocumentColumns}
              data={documentConfigList}
              paginationTotalRows={totalList || 0}
              onChangePage={(p) => setCurrentPage(p)}
              customStyles={tableCustomStyles}
              paginationComponent={PaginatorForRDT}
            />
          </BoxComponent>
        )}

        {tab === 2 && (isGridView ?
          <>
            <BoxComponent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {declarationConfigList?.map((row: any) => {
                  return (
                    <div className="p-4 rounded-2xl bg-white flex flex-col shadow-lg border border-gray-300">
                      <div className="flex justify-between items-center space-x-4 mb-4">
                        <button className={row.active === 1 ? "activeButtonCss" : "inactiveButtonCss"}>
                          {row.active === 1 ? "Active" : "Inactive"}
                        </button>
                        {/* <img src={Edit} alt="Edit" onClick={() => navigate("/new-declaration", { state: { declarationData: row } })} className="globalEditCss" /> */}
                        <div className="flex space-x-2">
                          <EditButton
                            onClick={() => navigate("/new-declaration", { state: { declarationData: row } })}
                          />
                          <ViewButton
                            disabled={true}
                            onClick={function (): void {
                              throw new Error("Function not implemented.");
                            }}
                          />
                        </div>
                      </div>
                      <div className="mb-2 flex text-[12px]">
                        <p className="font-bold text-base w-1/2">Declaration Name</p>
                        <p className="text-base mx-2">:</p>
                        <p className="text-base w-2/3">{row?.declaration_name}</p>
                      </div>
                      <div className="mb-2 flex text-[12px]">
                        <p className="font-bold text-base w-1/2">Form Type</p>
                        <p className="text-base mx-2">:</p>
                        <p className="text-base w-2/3">{row?.form_type ?? ''}</p>
                      </div>
                      <div className="mb-2 flex text-[12px]">
                        <p className="font-bold text-base w-1/2">Last Publish Date</p>
                        <p className="text-base mx-2">:</p>
                        <p className="text-base w-2/3">{formatDate(row?.updated_date)}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
              <Paginator
                limit={entriesPerPage}
                page={currentPage}
                setPage={setCurrentPage}
                count={totalList || 0}
              />
            </BoxComponent>
          </>
          : <BoxComponent>
            <Table
              columns={DeclarationColumns}
              data={declarationConfigList}
              paginationTotalRows={totalList || 0}
              onChangePage={(p) => setCurrentPage(p)}
              customStyles={tableCustomStyles}
              paginationComponent={PaginatorForRDT}
            />
          </BoxComponent>)}
      </div>
    </div>
    // </div >
  );
};

export default DocumentConfig;